import { attachInstallIdToUrl, Completion, Interaction } from '@youversion/analytics-youversion-nextjs';
import { Buffer } from 'buffer';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useTranslation } from 'next-i18next';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { ConfirmAppleAccount } from '@/components/ConfirmAppleAccount';
import GoogleButton from '@/components/GoogleButton';
import { IS_STAGING, LOGIN_CLIENT_ID, LOGIN_CLIENT_SECRET } from '@/env';
import { useQueryParams } from '@/hooks';
import { authentication, initialize, redirectWithToken } from '@/lib/web-auth-browser';
import { ThirdPartyTokenProvider, TokenClass } from '@/lib/web-auth-core';
import { ThirdPartyProps } from '@/pages/create-account';
import { analyticsElementIds } from '@/utils/analytics/elements';
import { CreateAccount, CreateAccountValues } from '@/utils/auth/createAccount';
import { APPID, APPLE_REDIRECT_URL } from '@/utils/constants';
import { useConstructUrl } from '@/utils/construct-url';
import { DEFAULT_TARGET_APPLICATION, getClientSecret, isValidRedirectUri } from '@/utils/supported-redirects';

import FacebookImg from '../../../public/images/facebook.svg';
import { AppleIcon } from '../../design-system/Apple';

declare global {
  interface Window {
    onLoginWithGoogle?: IdConfiguration['callback'];
  }
}

type TrackAuthErrorParams = {
  action: 'login' | 'sign_up';
  error: unknown;
  platform: 'google' | 'facebook' | 'apple';
};

interface Props {
  isAppleQuery: Dispatch<SetStateAction<boolean>>;
  isConfirmingApple: boolean;
}

export default function ThirdPartyOptions({ isAppleQuery, isConfirmingApple }: Props) {
  const { t, i18n } = useTranslation(['common', 'createAccount']);
  const router = useRouter();

  const [error, setError] = useState<string | null>(null);
  const [isVerifyAppleEmail, setIsVerifyAppleEmail] = useState(false);
  const { app_ref: appRef, client_id: clientIdParam, redirect_uri: redirectUriParam, scopes } = useQueryParams();

  const verifyEmailUrl = useConstructUrl('/verify-email');

  const loginForRedirect = useCallback(async () => {
    // If no params are provided, we want to redirect to the user to bible.com, the default target of login.youversion.com
    const clientId = clientIdParam ?? DEFAULT_TARGET_APPLICATION?.id;
    const clientSecret = getClientSecret(clientId) ?? DEFAULT_TARGET_APPLICATION?.secret;
    const redirectUri = redirectUriParam ?? DEFAULT_TARGET_APPLICATION?.redirect;
    try {
      if (clientId && clientSecret && redirectUri && isValidRedirectUri({ clientId, redirectUri })) {
        const tokenForRedirect = await authentication.loginWithTokenForRedirect({
          clientId,
          clientSecret,
          scopes: scopes ? scopes.split(' ') : [],
        });
        redirectWithToken(tokenForRedirect, attachInstallIdToUrl(redirectUri));
        return;
      }
      await router.push('/profile');
    } catch {
      await router.push('/profile');
    }
  }, [clientIdParam, redirectUriParam, router, scopes]);

  /**
   * This Function checks if authentication is available. If not, initialize the app for authentication checks.
   */
  const ensureInitialized = useCallback(async () => {
    if (authentication) {
      return;
    }
    let token = await initialize({ clientId: LOGIN_CLIENT_ID, clientSecret: LOGIN_CLIENT_SECRET, staging: IS_STAGING });
    if (token) {
      return;
    }
  }, []);

  const getErrorMessage = useCallback(
    (error: unknown) => {
      if (error instanceof Error) {
        if (error.message.startsWith('Invalid request: 403')) {
          return t('common:errors.generic');
        } else if (
          error.message.startsWith('Invalid request: 401') ||
          error.message.startsWith('Failed to refresh token')
        ) {
          return t('common:errors.invalid_credentials');
        } else {
          return error.message;
        }
      } else {
        return t('common:errors.generic');
      }
    },
    [t]
  );

  const onError = useCallback(
    (error: unknown) => {
      console.error(error);
      setError(getErrorMessage(error));
    },
    [getErrorMessage]
  );

  const trackAuthError = useCallback(
    ({ action, error, platform }: TrackAuthErrorParams) => {
      Completion.Auth.fire({
        authType: platform,
        completionContext: action,
        error: getErrorMessage(error),
        percentComplete: 0,
        success: false,
        userId: null,
      });
    },
    [getErrorMessage]
  );

  const loginWithThirdParty = useCallback(
    async (provider: ThirdPartyTokenProvider) => {
      const token = await authentication.loginWithThirdParty(provider);
      await loginForRedirect();
      return token;
    },
    [loginForRedirect]
  );

  const createThirdPartyAccount = useCallback(
    async ({ tokenType, tokenValue, values }: ThirdPartyProps) => {
      const res = await CreateAccount({
        appRef,
        languageTag: i18n.language,
        token: {
          type: tokenType,
          value: tokenValue,
        },
        values: values,
      });
      if (isVerifyAppleEmail) {
        localStorage.setItem('email', values.email);
        await router.push(verifyEmailUrl);
      } else if (res.status === 400) {
        await router.push({
          pathname: '/',
          query: {
            accountCreationError: true,
            type: 'add_account',
          },
        });

        let platform: TrackAuthErrorParams['platform'];
        switch (tokenType) {
          case 'Apple':
            platform = 'apple';
            break;

          case 'Facebook':
            platform = 'facebook';
            break;

          case 'Google':
            platform = 'google';
            break;
        }

        if (platform) {
          trackAuthError({
            action: 'sign_up',
            error: t('createAccount:errors.account_creation'),
            platform,
          });
        }
      }
    },
    [appRef, i18n.language, isVerifyAppleEmail, router, t, trackAuthError, verifyEmailUrl]
  );

  if (typeof window !== 'undefined') {
    window.fbAsyncInit = function () {
      try {
        FB.init({
          appId: APPID,
          autoLogAppEvents: true,
          status: true,
          version: 'v8.0',
          xfbml: true,
        });
      } catch (e) {
        onError(e);
      }
    };
  }

  useEffect(() => {
    window.onLoginWithGoogle = async response => {
      // We are intentionally not firing the Interaction event.
      // This is because the Google button is not a button element, but an iframe.
      // The iframe is rendered by Google's API, and we have no control over firing
      // an event on any specific `on*` attributes.
      let isNewUser = false;

      try {
        await ensureInitialized();
        const jwtToken = response.credential;
        const jwtTokenParts = jwtToken.split('.');
        const jwtTokenDetails = JSON.parse(Buffer.from(jwtTokenParts[1], 'base64').toString('utf-8'));
        const provider = {
          getToken: () => jwtToken,
          getTokenClass: () => TokenClass.google,
          getUserId: () => jwtTokenDetails.sub,
        } as const;
        try {
          const token = await loginWithThirdParty(provider);

          Completion.Auth.fire({
            authType: 'google',
            completionContext: 'login',
            success: true,
            userId: token.user.id,
          });
        } catch (e) {
          const isAccountNotFoundError = e instanceof Error && e.message === 'Invalid request: 403';
          if (!isAccountNotFoundError) {
            throw e;
          }

          isNewUser = true;

          const values: CreateAccountValues = {
            email: jwtTokenDetails.email,
            firstName: jwtTokenDetails['family_name'],
            lastName: jwtTokenDetails['given_name'],
            password: jwtToken,
            sub: jwtTokenDetails['sub'],
            token: {
              type: 'Google',
              value: jwtToken,
            },
          };
          await createThirdPartyAccount({ tokenType: 'Google', tokenValue: jwtToken, values });
          const token = await loginWithThirdParty(provider);
          Completion.Auth.fire({
            authType: 'google',
            completionContext: 'sign_up',
            success: true,
            userId: token.user.id,
          });
        }
      } catch (error) {
        onError(error);

        trackAuthError({
          action: isNewUser ? 'sign_up' : 'login',
          error,
          platform: 'google',
        });
      }
    };
  }, [createThirdPartyAccount, ensureInitialized, loginWithThirdParty, onError, trackAuthError]);

  function onClickFacebookLogin() {
    Interaction.None.fire({
      elementId: analyticsElementIds.facebookBtn,
      elementState: 'selecting',
      success: true,
    });

    FB.login(response => handleFacebookLoginResponse(response), { scope: 'public_profile,email' });
  }
  async function handleFacebookLoginResponse(response: fb.StatusResponse) {
    if (response.authResponse) {
      FB.api(
        '/me?fields=email,first_name,last_name',
        async function (facebookResponse: { email: string; first_name: string; id: string; last_name: string }) {
          let isNewUser = false;

          try {
            await ensureInitialized();
            const provider = {
              getToken: () => response.authResponse.accessToken as string,
              getTokenClass: () => TokenClass.facebook,
              getUserId: () => facebookResponse.id,
            };
            try {
              const token = await loginWithThirdParty(provider);

              Completion.Auth.fire({
                authType: 'facebook',
                completionContext: 'login',
                success: true,
                userId: token.user.id,
              });
            } catch (e) {
              isNewUser = true;

              const values: CreateAccountValues = {
                email: facebookResponse.email,
                firstName: facebookResponse.first_name,
                lastName: facebookResponse.last_name,
                password: response.authResponse.accessToken as string,
                token: {
                  type: 'Facebook',
                  value: response.authResponse.accessToken as string,
                },
              };
              await createThirdPartyAccount({
                tokenType: 'Facebook',
                tokenValue: response.authResponse.accessToken as string,
                values: values,
              });

              const token = await loginWithThirdParty(provider);
              Completion.Auth.fire({
                authType: 'facebook',
                completionContext: 'sign_up',
                success: true,
                userId: token.user.id,
              });
            }
          } catch (error) {
            onError(error);

            trackAuthError({
              action: isNewUser ? 'sign_up' : 'login',
              error,
              platform: 'facebook',
            });
          }
        }
      );
    } else {
      const errorMessage = 'Error! Please try to log in again!';
      onError(errorMessage);

      trackAuthError({
        action: 'login',
        error: errorMessage,
        platform: 'facebook',
      });
    }
  }

  // Creating an Account Via Apple
  function confirmAppleAccount() {
    const currentUrl = router.asPath;
    const confirmAppleUrl = currentUrl.includes('?')
      ? `${currentUrl}&confirm_apple_account=true`
      : `${currentUrl}?confirm_apple_account=true`;

    isAppleQuery(true);
    router.push(confirmAppleUrl, undefined, { shallow: true });
  }

  const accountValues = useRef<CreateAccountValues | null>(null);

  async function onLoginWithApple() {
    Interaction.None.fire({
      elementId: analyticsElementIds.appleBtn,
      elementState: 'selecting',
      success: true,
    });

    let isNewUser = false;

    try {
      // @ts-expect-error
      AppleID.auth.init({
        clientId: 'com.bible.login',
        redirectURI: APPLE_REDIRECT_URL,
        scope: 'name email',
        usePopup: true,
      });
      // @ts-expect-error
      const data = await AppleID.auth.signIn();
      const jwtToken = data.authorization.id_token;
      const jwtTokenParts = jwtToken.split('.');
      const jwtTokenDetails = JSON.parse(Buffer.from(jwtTokenParts[1], 'base64').toString('utf-8'));
      if (data.user) {
        isNewUser = true;
        if (jwtTokenDetails.is_private_email) {
          accountValues.current = {
            email: '',
            firstName: '',
            lastName: '',
            password: jwtToken,
            sub: jwtTokenDetails.sub,
            token: {
              type: 'Apple',
              value: jwtToken,
            },
          };
          setIsVerifyAppleEmail(true);
          confirmAppleAccount();
        } else {
          const values: CreateAccountValues = {
            email: data.user.email,
            firstName: data.user.name.firstName,
            lastName: data.user.name.lastName,
            password: jwtToken,
            sub: jwtTokenDetails.sub,
            token: {
              type: 'Apple',
              value: jwtToken,
            },
          };
          await createThirdPartyAccount({ tokenType: 'Apple', tokenValue: jwtToken, values: values });
        }
      }
      await ensureInitialized();
      const provider = {
        getToken: () => jwtToken,
        getTokenClass: () => TokenClass.apple,
        getUserId: () => jwtTokenDetails.sub,
      } as const;
      const token = await loginWithThirdParty(provider);
      Completion.Auth.fire({
        authType: 'apple',
        completionContext: isNewUser ? 'sign_up' : 'login',
        success: true,
        userId: token.user.id,
      });
    } catch (error) {
      onError(error);

      trackAuthError({
        action: isNewUser ? 'sign_up' : 'login',
        error,
        platform: 'apple',
      });
    }
  }

  return (
    <>
      {isConfirmingApple ? (
        <ConfirmAppleAccount
          handleSubmit={async values =>
            await createThirdPartyAccount({
              tokenType: 'Apple',
              tokenValue: accountValues.current?.token?.value ?? '',
              values: {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                password: accountValues.current?.password,
                sub: accountValues.current?.sub,
                token: accountValues.current?.token,
              },
            })
          }
        />
      ) : (
        <div className='flex flex-col gap-3 mbs-2'>
          {error ? <div className='bg-orange-10 text-orange-40 rounded p-2 mbs-4'>{error}</div> : null}
          <GoogleButton />

          <button className='btn-social bg-white text-text-light dark:hover:text-text-dark' onClick={onLoginWithApple}>
            <AppleIcon className='text-text-light dark:text-text-dark' /> {t('common:sign_in_with_apple')}
          </button>

          <button
            className='btn-social bg-white text-text-light dark:hover:text-text-dark'
            onClick={onClickFacebookLogin}
          >
            <FacebookImg /> {t('common:sign_in_with_facebook')}
          </button>

          <Script src='https://apis.google.com/js/platform.js' strategy='lazyOnload' />
          <Script
            src='https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
            type='text/javascript'
          />
          <Script async={true} crossOrigin='anonymous' defer={true} src='https://connect.facebook.net/en_US/sdk.js' />
        </div>
      )}
    </>
  );
}
